import { Menu } from '@base-ui/react/menu';
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Icon } from './icon';

export function DropdownMenu(props: React.ComponentProps<typeof Menu.Root>) {
  return <Menu.Root {...props} />;
}
export const DropdownMenuGroup = Menu.Group;

export function DropdownMenuTrigger({
  className,
  asChild,
  children,
  ...props
}: React.ComponentProps<typeof Menu.Trigger> & { asChild?: boolean }) {
  if (asChild && React.isValidElement(children)) {
    return <Menu.Trigger render={children} className={cn('w-full', className)} {...props} />;
  }
  return (
    <Menu.Trigger className={cn('w-full', className)} {...props}>
      {children}
    </Menu.Trigger>
  );
}
export const DropdownMenuRadioGroup = Menu.RadioGroup;
export const DropdownMenuSub = Menu.SubmenuRoot;

export function DropdownMenuContent({
  className,
  sideOffset = 4,
  align = 'start',
  children,
  ...props
}: React.ComponentProps<typeof Menu.Popup> & {
  sideOffset?: number;
  align?: 'start' | 'center' | 'end';
}) {
  return (
    <Menu.Portal>
      <Menu.Positioner sideOffset={sideOffset} align={align}>
        <Menu.Popup
          className={cn(
            'z-50 min-w-[8rem] border border-mesa-border bg-mesa-card p-1 shadow-md',
            'data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 transition-all',
            className
          )}
          {...props}
        >
          {children}
        </Menu.Popup>
      </Menu.Positioner>
    </Menu.Portal>
  );
}

export function DropdownMenuItem({
  className,
  inset,
  ...props
}: React.ComponentProps<typeof Menu.Item> & { inset?: boolean }) {
  return (
    <Menu.Item
      className={cn(
        'flex cursor-default items-center gap-2 px-2 py-1.5 text-sm outline-none',
        'data-[highlighted]:bg-mesa-cream-dark',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        inset && 'pl-8',
        className
      )}
      {...props}
    />
  );
}

export function DropdownMenuCheckboxItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof Menu.CheckboxItem>) {
  return (
    <Menu.CheckboxItem
      className={cn(
        'relative flex cursor-default items-center py-1.5 pr-2 pl-8 text-sm outline-none',
        'data-[highlighted]:bg-mesa-cream-dark',
        className
      )}
      {...props}
    >
      <span className="absolute left-2 flex size-4 items-center justify-center">
        <Menu.CheckboxItemIndicator>
          <Icon name="check" solid />
        </Menu.CheckboxItemIndicator>
      </span>
      {children}
    </Menu.CheckboxItem>
  );
}

export function DropdownMenuRadioItem({ className, children, ...props }: React.ComponentProps<typeof Menu.RadioItem>) {
  return (
    <Menu.RadioItem
      className={cn(
        'relative flex cursor-default items-center py-1.5 pr-2 pl-8 text-sm outline-none',
        'data-[highlighted]:bg-mesa-cream-dark',
        className
      )}
      {...props}
    >
      <span className="absolute left-2 flex size-4 items-center justify-center">
        <Menu.RadioItemIndicator>
          <Icon name="circle" solid className="text-[6px]" />
        </Menu.RadioItemIndicator>
      </span>
      {children}
    </Menu.RadioItem>
  );
}

export function DropdownMenuLabel({ className, inset, ...props }: React.ComponentProps<'div'> & { inset?: boolean }) {
  return <div className={cn('px-2 py-1.5 text-sm font-medium', inset && 'pl-8', className)} {...props} />;
}

export function DropdownMenuSeparator({ className, ...props }: React.ComponentProps<typeof Menu.Separator>) {
  return <Menu.Separator className={cn('-mx-1 my-1 h-px bg-mesa-border', className)} {...props} />;
}

export function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: React.ComponentProps<typeof Menu.SubmenuTrigger> & { inset?: boolean }) {
  return (
    <Menu.SubmenuTrigger
      className={cn(
        'flex cursor-default items-center px-2 py-1.5 text-sm outline-none',
        'data-[highlighted]:bg-mesa-cream-dark data-[popup-open]:bg-mesa-cream-dark',
        inset && 'pl-8',
        className
      )}
      {...props}
    >
      {children}
      <Icon name="chevron-right" className="ml-auto" />
    </Menu.SubmenuTrigger>
  );
}

export function DropdownMenuSubContent({ className, ...props }: React.ComponentProps<typeof Menu.Popup>) {
  return (
    <Menu.Portal>
      <Menu.Positioner className="z-[100]">
        <Menu.Popup
          className={cn(
            'z-[100] min-w-[8rem] border border-mesa-border bg-mesa-card p-1 shadow-lg',
            'data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 transition-all',
            className
          )}
          {...props}
        />
      </Menu.Positioner>
    </Menu.Portal>
  );
}
